import { useMemo, useState } from 'react'
import { displayTable } from '../../utils/tableLabel'
import { formatTRY } from '../../utils/money'
import { formatClock } from '../../utils/dayKey'
import type { Order } from '../../types'

interface Props {
  openTickets: Order[]
  /** Loads the chosen ticket into the cart, ready to settle. */
  onPick: (ticket: Order) => void
  onCancel: () => void
}

/**
 * Finds a table from what the customer ate.
 *
 * Someone walks up to pay and does not know their table number. With twenty
 * tables open, the alternative is reading tickets aloud until one matches,
 * with a queue behind them. Here the cashier taps what the customer says they
 * had, and the tables that hold all of it are what remain.
 *
 * Narrowing rather than filtering: each tap has to be true of the table, so two
 * items usually settle it. Matching any one of them would leave "çay" selecting
 * half the room.
 */
export function FindTableDialog({ openTickets, onPick, onCancel }: Props) {
  const [picked, setPicked] = useState<Set<string>>(new Set())

  /** Every product on an open ticket, once each, in menu order per ticket. */
  const products = useMemo(() => {
    const seen = new Map<string, string>()
    for (const t of openTickets) {
      for (const l of t.lines) {
        if (!seen.has(l.productId)) seen.set(l.productId, l.name)
      }
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name }))
  }, [openTickets])

  /** Tables holding every product picked so far. */
  const matches = useMemo(() => {
    if (picked.size === 0) return openTickets
    return openTickets.filter((t) => {
      const onTicket = new Set(t.lines.map((l) => l.productId))
      for (const id of picked) if (!onTicket.has(id)) return false
      return true
    })
  }, [openTickets, picked])

  /**
   * Products still worth offering: the ones on a table that is still in the
   * running. Without this the cashier keeps tapping items that can only take
   * the list to nothing, which reads as the search being broken.
   */
  const usable = useMemo(() => {
    const live = new Set<string>()
    for (const t of matches) for (const l of t.lines) live.add(l.productId)
    return live
  }, [matches])

  const toggle = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Düğme artık her zaman görünüyor; aranacak masa yokken boş bir ürün
  // listesi yerine bunu söylüyor, Taşı penceresi gibi.
  if (openTickets.length === 0) {
    return (
      <div className="dlg" role="dialog" aria-modal="true">
        <div className="dlg__card dlg__card--find">
          <button type="button" className="split__x" onClick={onCancel} title="Kapat" aria-label="Kapat">
            ×
          </button>
          <h2>Masa bul</h2>
          <p className="dlg__empty">Şu anda açık adisyon yok.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="dlg" role="dialog" aria-modal="true">
      <div className="dlg__card dlg__card--find">
        {/* Kapatmak bir eylem değil, çıkış: köşede, Parçalı Öde'deki gibi. */}
        <button type="button" className="split__x" onClick={onCancel} title="Kapat" aria-label="Kapat">
          ×
        </button>
        <h2>Masa bul</h2>
        <p className="dlg__empty">
          Müşterinin ne yediğine dokunun. Hepsini içeren masalar aşağıda kalır.
        </p>

        <div className="findt__items">
          {products.map((p) => {
            const on = picked.has(p.id)
            // Dimmed rather than removed: a list that reshuffles under the hand
            // loses the item the cashier was reaching for.
            const dead = !on && !usable.has(p.id)
            return (
              <button
                key={p.id}
                type="button"
                className={`findt__item ${on ? 'is-on' : ''} ${dead ? 'is-dead' : ''}`}
                onClick={() => toggle(p.id)}
                aria-pressed={on}
              >
                {p.name}
              </button>
            )
          })}
        </div>

        <div className="findt__result">
          {matches.length === 0 ? (
            <p className="findt__none">
              Bu ürünlerin hepsini içeren masa yok. Bir seçimi geri alın.
            </p>
          ) : (
            matches.map((t) => (
              <button
                key={t.id}
                type="button"
                className="findt__row"
                onClick={() => onPick(t)}
              >
                <span className="findt__table">
                  {displayTable(t.tableName) ?? 'Masa yok'}
                  {t.openedByName && <span className="findt__by">, {t.openedByName}</span>}
                </span>
                <span className="findt__meta">
                  {t.itemCount} ürün, {formatClock(t.createdAtLocal)}
                </span>
                <span className="findt__total">{formatTRY(t.totalKurus)}</span>
              </button>
            ))
          )}
        </div>

        {picked.size > 0 && (
          <div className="dlg__buttons">
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => setPicked(new Set())}
            >
              Seçimi temizle
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
